import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type FarmsoOnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface FarmsoOnboardingState {
  status: FarmsoOnboardingStatus;
  completedSections: {
    farmProfile: boolean;
  };
  lastUpdated?: string;
}

interface FarmsoOnboardingContextType {
  onboardingState: FarmsoOnboardingState;
  updateOnboardingSection: (section: keyof FarmsoOnboardingState['completedSections'], isComplete: boolean) => void;
  isOnboardingComplete: boolean;
  getOnboardingProgress: () => { completed: number; total: number; percentage: number };
  forceCompleteOnboarding: () => void;
  resetOnboarding: () => void;
}

const FarmsoOnboardingContext = createContext<FarmsoOnboardingContextType | undefined>(undefined);

const STORAGE_KEY = 'farmso_onboarding_state';

export function FarmsoOnboardingProvider({ children }: { children: ReactNode }) {
  const [onboardingState, setOnboardingState] = useState<FarmsoOnboardingState>(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse Farmso onboarding state:', e);
      }
    }
    return {
      status: 'NOT_STARTED',
      completedSections: {
        farmProfile: false,
      },
    };
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(onboardingState));
  }, [onboardingState]);

  // Calculate if onboarding is complete
  const isOnboardingComplete = onboardingState.completedSections.farmProfile;

  // Update status based on completed sections
  useEffect(() => {
    if (isOnboardingComplete && onboardingState.status !== 'COMPLETED') {
      setOnboardingState(prev => ({
        ...prev,
        status: 'COMPLETED',
        lastUpdated: new Date().toISOString(),
      }));
    } else if (!isOnboardingComplete && onboardingState.status === 'COMPLETED') {
      setOnboardingState(prev => ({
        ...prev,
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString(),
      }));
    }
  }, [isOnboardingComplete, onboardingState.status]);

  const updateOnboardingSection = (
    section: keyof FarmsoOnboardingState['completedSections'],
    isComplete: boolean
  ) => {
    setOnboardingState(prev => ({
      ...prev,
      status: prev.status === 'NOT_STARTED' ? 'IN_PROGRESS' : prev.status,
      completedSections: {
        ...prev.completedSections,
        [section]: isComplete,
      },
      lastUpdated: new Date().toISOString(),
    }));
  };

  const getOnboardingProgress = () => {
    const total = 1; // Only farm profile
    const completed = Object.values(onboardingState.completedSections).filter(Boolean).length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const forceCompleteOnboarding = () => {
    setOnboardingState({
      status: 'COMPLETED',
      completedSections: {
        farmProfile: true,
      },
      lastUpdated: new Date().toISOString(),
    });
  };

  const resetOnboarding = () => {
    setOnboardingState({
      status: 'NOT_STARTED',
      completedSections: {
        farmProfile: false,
      },
      lastUpdated: new Date().toISOString(),
    });
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <FarmsoOnboardingContext.Provider
      value={{
        onboardingState,
        updateOnboardingSection,
        isOnboardingComplete,
        getOnboardingProgress,
        forceCompleteOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </FarmsoOnboardingContext.Provider>
  );
}

export function useFarmsoOnboarding() {
  const context = useContext(FarmsoOnboardingContext);
  if (context === undefined) {
    throw new Error('useFarmsoOnboarding must be used within a FarmsoOnboardingProvider');
  }
  return context;
}
